import { createSelector } from 'reselect'
import { createCommunicationSelectors } from 'src/helpers/redux'
import { actions } from './actions'

import {
  IBackendCall,
  IBackendCallParticipant,
  IState,
  PREFIX,
  TGlobalStateWithService,
} from './state'

/** Statuses of a participant who is still in the call, including one who is not connected yet */
const ACTIVE_PARTICIPANT_STATUSES: readonly IBackendCallParticipant['status'][] =
  ['initiated', 'ringing', 'connected']

/**
 * Participants other than the current Twilio leg.
 * The current leg is the one whose `callSid` matches the call in progress.
 */
export const getActiveOtherParticipants = (
  backendCall: IBackendCall | null,
  callSid: string | null
): IBackendCallParticipant[] =>
  (backendCall?.participants ?? []).filter(
    (participant) =>
      participant.callSid !== callSid &&
      ACTIVE_PARTICIPANT_STATUSES.includes(participant.status)
  )

/** Two or more other active participants: the call UI uses the group layout */
export const getIsGroupCall = (
  backendCall: IBackendCall | null,
  callSid: string | null
) => getActiveOtherParticipants(backendCall, callSid).length > 1

export const selectCommunication = (
  state: TGlobalStateWithService
): IState['communication'] => state[PREFIX].communication

export const communications = createCommunicationSelectors<
  TGlobalStateWithService,
  IState['communication'],
  typeof actions
>(selectCommunication, actions)

export const selectCall = (
  state: TGlobalStateWithService
): IState['data']['call'] => state[PREFIX].data.call

export const selectDevice = (
  state: TGlobalStateWithService
): IState['data']['device'] => state[PREFIX].data.device

export const selectParticipants = (
  state: TGlobalStateWithService
): IState['data']['participants'] => state[PREFIX].data.participants

export const selectCallSid = (
  state: TGlobalStateWithService
): IState['data']['callSid'] => state[PREFIX].data.callSid

export const selectBackendCall = (
  state: TGlobalStateWithService
): IState['data']['backendCall'] => state[PREFIX].data.backendCall

const emptySubscriberCallHistory: IState['data']['subscriberCallHistory'] = {}

export const selectSubscriberCallHistory = (
  state: TGlobalStateWithService
): IState['data']['subscriberCallHistory'] =>
  state[PREFIX].data.subscriberCallHistory ?? emptySubscriberCallHistory

export const selectSubscriberCallHistoryErrorUniqId = (
  state: TGlobalStateWithService
): IState['data']['subscriberCallHistoryErrorUniqId'] =>
  state[PREFIX].data.subscriberCallHistoryErrorUniqId

export const selectors = {
  call: createSelector(selectCall, (state) => state),
  device: createSelector(selectDevice, (state) => state),
  participants: createSelector(selectParticipants, (state) => state),
  callSid: createSelector(selectCallSid, (state) => state),
  backendCall: createSelector(selectBackendCall, (state) => state),
  subscriberCallHistory: createSelector(
    selectSubscriberCallHistory,
    (state) => state
  ),
  subscriberCallHistoryErrorUniqId: createSelector(
    selectSubscriberCallHistoryErrorUniqId,
    (state) => state
  ),
  activeOtherParticipants: createSelector(
    selectBackendCall,
    selectCallSid,
    getActiveOtherParticipants
  ),
  isGroupCall: createSelector(selectBackendCall, selectCallSid, getIsGroupCall),
}
